// LPG cars: which cars run on gas from which day, the standard each is held to,
// the price Fleetly pays at the Sasol in Spartan, and what each driver on an LPG
// car saved or overdrew this pay period. db/migrations/0383 and 0384.
//
// Every figure comes from lpg_page_v1. The page arranges them. CLAUDE.md rule 4.
//
// THE RULE, as the drivers' contract allows it. Held to 20 km/l, or the car's
// own standard where it cannot reach that. Gas saved below the standard is paid
// half to the driver as its own payslip line. Gas drawn above it comes off his
// Share Pool at the LPG price and stops there: clause 13.6 says a negative fuel
// adjustment "is never a deduction from wages and is never a debt". Above the 5%
// tolerance the page says what the contract says next, check the car first, and
// leaves the decision to a person.
//
// Reading is open to every staff login. Changing anything needs
// cockpit_access.can_mark_fuel_conversion, and Postgres checks it again.
import { useCallback, useEffect, useMemo, useState, type ReactNode } from 'react'
import { AlertTriangle, Fuel, Pencil, RotateCcw, Tag, X } from 'lucide-react'
import { useEndLpgConversion, useGetLpgPage, useRecordLpgConversion, useRecordLpgPrice } from '../hooks/backend/lpg'
import { Button } from '../lib/shadcn/button'
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from '../lib/shadcn/dialog'
import { Input } from '../lib/shadcn/input'
import { NativeSelect } from '../lib/shadcn/native-select'
import { Spinner } from '../lib/shadcn/spinner'
import { Textarea } from '../lib/shadcn/textarea'
import { Chip } from '../components/interviews/Chip'
import {
  formatDay, formatKmpl, formatNum, formatZar, toNumber,
  type LpgCar, type LpgDriver, type LpgPage,
} from '../utils/lpg'

type Saved = { ok: boolean; message?: string }

export default function LpgCars() {
  const page = useGetLpgPage<LpgPage>()
  const load = page.trigger
  const [notice, setNotice] = useState<string | null>(null)
  const [marking, setMarking] = useState<{ vehicleId: string | null } | null>(null)
  const [ending, setEnding] = useState<LpgCar | null>(null)
  const [pricing, setPricing] = useState(false)

  const refresh = useCallback(() => { void load({}, { skipCache: true }).catch(() => undefined) }, [load])
  useEffect(() => { refresh() }, [refresh])

  const data = page.data
  const onSaved = (r: Saved | null | undefined) => {
    if (r?.message) setNotice(r.message)
    refresh()
  }

  if (page.loading && !data && !page.error) {
    return <div className="flex h-full items-center justify-center gap-2 text-sm text-muted-foreground"><Spinner className="h-4 w-4" /> Loading LPG cars…</div>
  }
  if (!data) {
    return (
      <div className="flex h-full items-center justify-center p-6">
        <div className="max-w-md space-y-3 rounded-lg border border-border bg-card p-6 text-center text-sm">
          <p className="text-rose-700 dark:text-rose-300">{page.error ?? 'The LPG page could not be loaded.'}</p>
          <Button size="sm" variant="outline" onClick={refresh}>Try again</Button>
        </div>
      </div>
    )
  }

  const std = data.policy ? formatKmpl(data.policy.default_km_per_litre) : '—'
  const share = data.policy ? Math.round(Number(data.policy.driver_saving_share) * 100) : null
  const active = data.cars.filter((c) => !c.ended_on)
  const past = data.cars.filter((c) => c.ended_on)

  return (
    <div className="flex h-full min-h-0 flex-col bg-card">
      <div className="flex flex-wrap items-center gap-3 border-b border-border px-4 py-3">
        <div className="flex h-9 w-9 shrink-0 items-center justify-center rounded-lg bg-[hsl(var(--brand-soft))] text-[hsl(var(--brand-soft-foreground))]">
          <Fuel className="h-5 w-5" />
        </div>
        <div className="min-w-[12rem] flex-1">
          <h1 className="text-lg font-semibold text-foreground">LPG cars</h1>
          <p className="text-xs text-muted-foreground">
            Drivers on a converted car are held to {std}.
            {share !== null && ` Gas saved below that is paid ${share}% to the driver as its own payslip line.`}
            {' '}Gas drawn above it comes off his Share Pool, never off his wages.
          </p>
        </div>
        {data.can_edit && (
          <>
            <Button variant="outline" size="sm" onClick={() => setPricing(true)}>
              <Tag className="mr-1 h-4 w-4" /> New LPG price
            </Button>
            <Button variant="brand" size="sm" onClick={() => setMarking({ vehicleId: null })}>
              <Fuel className="mr-1 h-4 w-4" /> Mark a car as LPG
            </Button>
          </>
        )}
      </div>

      <div className="flex flex-wrap items-center gap-2 border-b border-border px-4 py-2">
        {data.price
          ? <Chip tone="brand" className="whitespace-normal">R{formatNum(data.price.price, 2)} a litre at {data.price.station}, from {formatDay(data.price.effective_from)}</Chip>
          : <Chip tone="red" icon={<AlertTriangle className="h-3 w-3" />}>No LPG price on record for today</Chip>}
        <Chip tone="neutral">Pay period {formatDay(data.period.start)} to {formatDay(data.period.end)}</Chip>
        <Chip tone="neutral">{active.length} car{active.length === 1 ? '' : 's'} on LPG</Chip>
      </div>

      {notice && (
        <div className="flex items-center gap-2 border-b border-border bg-muted/40 px-4 py-2 text-xs text-foreground">
          <span className="flex-1">{notice}</span>
          <button type="button" aria-label="Dismiss" onClick={() => setNotice(null)}><X className="h-3.5 w-3.5" /></button>
        </div>
      )}
      {page.error && <div className="border-b border-border bg-rose-50 px-4 py-2 text-sm text-rose-800 dark:bg-rose-950 dark:text-rose-200">{page.error}</div>}

      <div className="min-h-0 flex-1 space-y-6 overflow-y-auto px-4 py-4">
        <Section title="Cars on LPG" hint="Shift figures are for this pay period. Litres are what the shift form declared; the slips column is the gas actually bought on the car since it started on LPG.">
          {active.length === 0
            ? <Empty>No car is marked as LPG yet.{data.can_edit ? ' Use Mark a car as LPG above.' : ''}</Empty>
            : <CarTable cars={active} canEdit={data.can_edit}
                onChange={(c) => setMarking({ vehicleId: c.vehicle_id })} onEnd={(c) => setEnding(c)} />}
        </Section>

        <Section title="Drivers this pay period" hint="Netted across all his LPG shifts in the period, as clause 13.4 requires, never shift by shift.">
          {data.drivers.length === 0
            ? <Empty>No LPG shift has been paid in this period yet.</Empty>
            : <DriverTable drivers={data.drivers} />}
        </Section>

        {past.length > 0 && (
          <Section title="Back on petrol">
            <CarTable cars={past} canEdit={false} onChange={() => undefined} onEnd={() => undefined} />
          </Section>
        )}

        <Section title="LPG prices">
          <ul className="divide-y divide-border rounded-md border border-border text-sm">
            {data.prices.map((p) => (
              <li key={p.effective_from} className="flex flex-wrap items-center gap-x-4 gap-y-1 px-3 py-2">
                <span className="font-medium">R{formatNum(p.price, 2)}</span>
                <span className="text-muted-foreground">{p.station}</span>
                <span className="text-muted-foreground">
                  {formatDay(p.effective_from)}{p.effective_to ? ` to ${formatDay(p.effective_to)}` : ' onwards'}
                </span>
                {p.recorded_by && <span className="text-xs text-muted-foreground">recorded by {p.recorded_by}</span>}
              </li>
            ))}
          </ul>
        </Section>
      </div>

      {marking && (
        <MarkDialog page={data} presetVehicleId={marking.vehicleId}
          onClose={() => setMarking(null)} onSaved={(r) => { setMarking(null); onSaved(r) }} />
      )}
      {ending && (
        <EndDialog car={ending} today={data.today}
          onClose={() => setEnding(null)} onSaved={(r) => { setEnding(null); onSaved(r) }} />
      )}
      {pricing && (
        <PriceDialog page={data} onClose={() => setPricing(false)} onSaved={(r) => { setPricing(false); onSaved(r) }} />
      )}
    </div>
  )
}

function Section({ title, hint, children }: { title: string; hint?: string; children: ReactNode }) {
  return (
    <section className="space-y-2">
      <div>
        <h2 className="text-sm font-semibold text-foreground">{title}</h2>
        {hint && <p className="text-xs text-muted-foreground">{hint}</p>}
      </div>
      {children}
    </section>
  )
}

function Empty({ children }: { children: ReactNode }) {
  return <p className="rounded-md border border-dashed border-border px-3 py-6 text-center text-sm text-muted-foreground">{children}</p>
}

function Th({ children, right }: { children: ReactNode; right?: boolean }) {
  return <th className={`px-3 py-2 text-xs font-medium text-muted-foreground ${right ? 'text-right' : 'text-left'}`}>{children}</th>
}

function Td({ children, right, className = '' }: { children: ReactNode; right?: boolean; className?: string }) {
  return <td className={`px-3 py-2 align-top ${right ? 'text-right tabular-nums' : ''} ${className}`}>{children}</td>
}

function CarTable({ cars, canEdit, onChange, onEnd }: {
  cars: LpgCar[]; canEdit: boolean; onChange: (c: LpgCar) => void; onEnd: (c: LpgCar) => void
}) {
  return (
    <div className="overflow-x-auto rounded-md border border-border">
      <table className="w-full min-w-[56rem] text-sm">
        <thead className="border-b border-border bg-muted/40">
          <tr>
            <Th>Car</Th><Th>On LPG</Th><Th>Held to</Th>
            <Th right>Shifts</Th><Th right>Km</Th><Th right>Litres</Th><Th right>km/l</Th>
            <Th right>Gas cost</Th><Th right>Against standard</Th><Th>Slips</Th>
            {canEdit && <Th> </Th>}
          </tr>
        </thead>
        <tbody className="divide-y divide-border">
          {cars.map((c) => {
            const saved = toNumber(c.period.value_saved_zar)
            return (
              <tr key={c.id}>
                <Td>
                  <div className="font-medium">{c.plate}</div>
                  <div className="text-xs text-muted-foreground">{[c.make, c.model].filter(Boolean).join(' ')}</div>
                </Td>
                <Td>
                  <div>{formatDay(c.started_on)}{c.ended_on ? ` to ${formatDay(c.ended_on)}` : ' onwards'}</div>
                  {c.recorded_by && <div className="text-xs text-muted-foreground">by {c.recorded_by}</div>}
                  {c.end_reason && <div className="text-xs text-muted-foreground">{c.end_reason}</div>}
                </Td>
                <Td>
                  <div>{formatKmpl(c.km_per_litre_standard)}</div>
                  {c.standard_reason && <div className="max-w-[14rem] text-xs text-muted-foreground">{c.standard_reason}</div>}
                </Td>
                <Td right>{c.period.shifts}</Td>
                <Td right>{formatNum(c.period.kilometres, 0)}</Td>
                <Td right>{formatNum(c.period.litres, 1)}</Td>
                <Td right>{formatNum(c.period.km_per_litre, 1)}</Td>
                <Td right>{formatZar(c.period.cost_zar)}</Td>
                <Td right className={saved === null ? '' : saved >= 0 ? 'text-emerald-700 dark:text-emerald-300' : 'text-rose-700 dark:text-rose-300'}>
                  {saved === null ? '—' : `${saved >= 0 ? 'saved ' : 'over by '}${formatZar(Math.abs(saved))}`}
                </Td>
                <Td><SlipCheck car={c} /></Td>
                {canEdit && (
                  <Td>
                    <div className="flex justify-end gap-1">
                      <Button size="sm" variant="ghost" onClick={() => onChange(c)} title="Change the standard or the start day">
                        <Pencil className="h-3.5 w-3.5" />
                      </Button>
                      <Button size="sm" variant="ghost" onClick={() => onEnd(c)} title="Back on petrol">
                        <RotateCcw className="h-3.5 w-3.5" />
                      </Button>
                    </div>
                  </Td>
                )}
              </tr>
            )
          })}
        </tbody>
      </table>
    </div>
  )
}

// The shift form's km/l is a declaration. On a converted car the dashboard may
// still be reading the petrol injectors, so the gas bought is the check on it.
function SlipCheck({ car }: { car: LpgCar }) {
  const s = car.slips
  const bought = toNumber(s.lpg_litres)
  const declared = toNumber(s.declared_litres)
  return (
    <div className="space-y-0.5 text-xs">
      {s.lpg_lines === 0
        ? <div className="text-muted-foreground">No gas slip yet</div>
        : <div>{formatNum(bought, 1)} l gas bought on {s.lpg_lines} slip{s.lpg_lines === 1 ? '' : 's'}</div>}
      <div className="text-muted-foreground">{declared === null ? 'No litres declared yet' : `${formatNum(declared, 1)} l declared on shift forms`}</div>
      {s.other_lines > 0 && (
        <div className="text-amber-700 dark:text-amber-300">
          {formatNum(s.other_litres, 1)} l of other fuel on {s.other_lines} slip{s.other_lines === 1 ? '' : 's'}
        </div>
      )}
    </div>
  )
}

function DriverTable({ drivers }: { drivers: LpgDriver[] }) {
  return (
    <div className="overflow-x-auto rounded-md border border-border">
      <table className="w-full min-w-[48rem] text-sm">
        <thead className="border-b border-border bg-muted/40">
          <tr>
            <Th>Driver</Th><Th right>Shifts</Th><Th right>Km</Th><Th right>Litres</Th>
            <Th right>km/l</Th><Th right>Standard litres</Th><Th>What it means for his pay</Th>
          </tr>
        </thead>
        <tbody className="divide-y divide-border">
          {drivers.map((d) => {
            const adj = toNumber(d.adjustment_zar) ?? 0
            return (
              <tr key={d.driver_id}>
                <Td className="font-medium">{d.name}</Td>
                <Td right>{d.shifts}</Td>
                <Td right>{formatNum(d.kilometres, 0)}</Td>
                <Td right>{formatNum(d.litres, 2)}</Td>
                <Td right>{formatNum(d.km_per_litre, 1)}</Td>
                <Td right>{formatNum(d.litres_standard, 2)}</Td>
                <Td>
                  {adj > 0 && (
                    <div className="text-emerald-700 dark:text-emerald-300">
                      {formatZar(d.saving_share_zar)} paid to him, half of the {formatZar(adj)} of gas he saved
                    </div>
                  )}
                  {adj < 0 && (
                    <div className="text-rose-700 dark:text-rose-300">
                      {formatZar(Math.abs(adj))} of gas above the standard comes off his Share Pool
                    </div>
                  )}
                  {adj === 0 && <div className="text-muted-foreground">On the standard exactly</div>}
                  {d.above_tolerance && (
                    <div className="mt-1 flex items-start gap-1 text-xs text-amber-700 dark:text-amber-300">
                      <AlertTriangle className="mt-0.5 h-3 w-3 shrink-0" />
                      <span>
                        {formatNum(d.l_per_100km, 2)} l/100km is above the {formatNum(d.tolerance_l_per_100km, 2)} tolerance.
                        Check the car, tyres, load and area first (clause 13.7).
                        {d.performance_eligible
                          ? ' If none explains it, it is a ground for the performance process (clause 26.7).'
                          : ' Fewer than eight shifts, so no performance step can be taken on this period (clause 26.2.2).'}
                      </span>
                    </div>
                  )}
                </Td>
              </tr>
            )
          })}
        </tbody>
      </table>
    </div>
  )
}

function Field({ label, hint, htmlFor, children }: { label: string; hint?: ReactNode; htmlFor: string; children: ReactNode }) {
  return (
    <div className="space-y-1">
      <label htmlFor={htmlFor} className="text-xs font-medium text-foreground">{label}</label>
      {children}
      {hint && <p className="text-[11px] text-muted-foreground">{hint}</p>}
    </div>
  )
}

function MarkDialog({ page, presetVehicleId, onClose, onSaved }: {
  page: LpgPage; presetVehicleId: string | null; onClose: () => void; onSaved: (r: Saved) => void
}) {
  const save = useRecordLpgConversion<Saved>()
  const defaultStd = page.policy ? String(Number(page.policy.default_km_per_litre)) : '20'
  const existing = presetVehicleId ? page.cars.find((c) => c.vehicle_id === presetVehicleId && !c.ended_on) : undefined
  const [vehicleId, setVehicleId] = useState(presetVehicleId ?? '')
  const [startedOn, setStartedOn] = useState(existing?.started_on ?? page.today)
  const [kmpl, setKmpl] = useState(existing ? String(Number(existing.km_per_litre_standard)) : defaultStd)
  const [reason, setReason] = useState(existing?.standard_reason ?? '')
  const [notes, setNotes] = useState('')
  const [error, setError] = useState<string | null>(null)

  const differs = kmpl.trim() !== '' && Number(kmpl.replace(',', '.')) !== Number(defaultStd)
  const vehicles = useMemo(() => page.vehicles, [page.vehicles])

  async function submit() {
    setError(null)
    try {
      const r = await save.trigger({
        vehicle_id: vehicleId, started_on: startedOn, km_per_litre: kmpl,
        standard_reason: differs ? reason : '', notes,
      })
      if (r) onSaved(r)
    } catch (e) {
      setError(e instanceof Error ? e.message : String(e))
    }
  }

  return (
    <Dialog open onOpenChange={(o) => { if (!o && !save.loading) onClose() }}>
      <DialogContent className="max-h-[92vh] max-w-md overflow-y-auto">
        <DialogHeader>
          <DialogTitle>{existing ? `Change ${existing.plate}` : 'Mark a car as LPG'}</DialogTitle>
          <DialogDescription>
            Shifts from the start day are priced at the LPG price and paid under the LPG rule. Shifts before it stay petrol.
          </DialogDescription>
        </DialogHeader>
        <div className="space-y-3">
          <Field label="Car" htmlFor="lpg-car">
            <NativeSelect id="lpg-car" value={vehicleId} onChange={(e) => setVehicleId(e.target.value)} disabled={Boolean(existing)}>
              <option value="">Choose the car that was converted</option>
              {vehicles.map((v) => (
                <option key={v.id} value={v.id}>
                  {v.plate}{v.make || v.model ? `, ${[v.make, v.model].filter(Boolean).join(' ')}` : ''}{v.on_lpg ? ' (on LPG)' : ''}
                </option>
              ))}
            </NativeSelect>
          </Field>
          <Field label="Day it started running on LPG" htmlFor="lpg-start"
            hint={existing ? 'A later day keeps the earlier period and starts a new one from this day, which is how a changed standard keeps its date.' : undefined}>
            <Input id="lpg-start" type="date" value={startedOn} max={page.today} onChange={(e) => setStartedOn(e.target.value)} />
          </Field>
          <Field label="Standard, km per litre" htmlFor="lpg-kmpl"
            hint={`The LPG standard is ${defaultStd} km/l. Lower it only for a car that cannot reach it, and say why.`}>
            <Input id="lpg-kmpl" inputMode="decimal" value={kmpl} onChange={(e) => setKmpl(e.target.value)} />
          </Field>
          {differs && (
            <Field label="Why this car has its own standard" htmlFor="lpg-reason" hint="The drivers in this car are paid and assessed against it, so they need to be able to read why.">
              <Textarea id="lpg-reason" rows={2} value={reason} onChange={(e) => setReason(e.target.value)} />
            </Field>
          )}
          <Field label="Notes (optional)" htmlFor="lpg-notes">
            <Textarea id="lpg-notes" rows={2} value={notes} onChange={(e) => setNotes(e.target.value)} placeholder="Who fitted the kit, invoice number" />
          </Field>
          {error && <p className="text-sm text-rose-700 dark:text-rose-300">{error}</p>}
          <div className="flex justify-end gap-2 pt-1">
            <Button variant="outline" size="sm" onClick={onClose} disabled={save.loading}>Cancel</Button>
            <Button variant="brand" size="sm" onClick={() => void submit()} disabled={save.loading || !vehicleId}>
              {save.loading && <Spinner className="mr-1 h-4 w-4" />} Save
            </Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  )
}

function EndDialog({ car, today, onClose, onSaved }: { car: LpgCar; today: string; onClose: () => void; onSaved: (r: Saved) => void }) {
  const end = useEndLpgConversion<Saved>()
  const [endedOn, setEndedOn] = useState(today)
  const [reason, setReason] = useState('')
  const [error, setError] = useState<string | null>(null)

  async function submit() {
    setError(null)
    try {
      const r = await end.trigger({ id: car.id, ended_on: endedOn, reason })
      if (r) onSaved(r)
    } catch (e) {
      setError(e instanceof Error ? e.message : String(e))
    }
  }

  return (
    <Dialog open onOpenChange={(o) => { if (!o && !end.loading) onClose() }}>
      <DialogContent className="max-h-[92vh] max-w-md overflow-y-auto">
        <DialogHeader>
          <DialogTitle>{car.plate} back on petrol</DialogTitle>
          <DialogDescription>Shifts after the last LPG day are priced and paid as petrol again. The LPG period is kept.</DialogDescription>
        </DialogHeader>
        <div className="space-y-3">
          <Field label="Last day on LPG" htmlFor="lpg-end">
            <Input id="lpg-end" type="date" value={endedOn} min={car.started_on} onChange={(e) => setEndedOn(e.target.value)} />
          </Field>
          <Field label="Why" htmlFor="lpg-end-reason">
            <Textarea id="lpg-end-reason" rows={2} value={reason} onChange={(e) => setReason(e.target.value)} placeholder="The kit was removed, or failed" />
          </Field>
          {error && <p className="text-sm text-rose-700 dark:text-rose-300">{error}</p>}
          <div className="flex justify-end gap-2 pt-1">
            <Button variant="outline" size="sm" onClick={onClose} disabled={end.loading}>Cancel</Button>
            <Button variant="brand" size="sm" onClick={() => void submit()} disabled={end.loading}>
              {end.loading && <Spinner className="mr-1 h-4 w-4" />} Save
            </Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  )
}

function PriceDialog({ page, onClose, onSaved }: { page: LpgPage; onClose: () => void; onSaved: (r: Saved) => void }) {
  const save = useRecordLpgPrice<Saved>()
  const [from, setFrom] = useState(page.today)
  const [price, setPrice] = useState('')
  const [station, setStation] = useState(page.price?.station ?? 'Sasol Spartan, Kempton Park')
  const [error, setError] = useState<string | null>(null)

  async function submit() {
    setError(null)
    try {
      const r = await save.trigger({ effective_from: from, price, station })
      if (r) onSaved(r)
    } catch (e) {
      setError(e instanceof Error ? e.message : String(e))
    }
  }

  return (
    <Dialog open onOpenChange={(o) => { if (!o && !save.loading) onClose() }}>
      <DialogContent className="max-h-[92vh] max-w-md overflow-y-auto">
        <DialogHeader>
          <DialogTitle>New LPG price</DialogTitle>
          <DialogDescription>
            {page.price ? `Now R${formatNum(page.price.price, 2)} a litre. ` : ''}Every LPG shift from the day you enter is repriced.
          </DialogDescription>
        </DialogHeader>
        <div className="space-y-3">
          <Field label="Price per litre, rand" htmlFor="lpg-price">
            <Input id="lpg-price" inputMode="decimal" value={price} onChange={(e) => setPrice(e.target.value)} placeholder="13.45" />
          </Field>
          <Field label="From" htmlFor="lpg-price-from">
            <Input id="lpg-price-from" type="date" value={from} max={page.today} onChange={(e) => setFrom(e.target.value)} />
          </Field>
          <Field label="Station" htmlFor="lpg-station">
            <Input id="lpg-station" value={station} onChange={(e) => setStation(e.target.value)} />
          </Field>
          {error && <p className="text-sm text-rose-700 dark:text-rose-300">{error}</p>}
          <div className="flex justify-end gap-2 pt-1">
            <Button variant="outline" size="sm" onClick={onClose} disabled={save.loading}>Cancel</Button>
            <Button variant="brand" size="sm" onClick={() => void submit()} disabled={save.loading || !price.trim()}>
              {save.loading && <Spinner className="mr-1 h-4 w-4" />} Save
            </Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  )
}
